import {
  WSEmptyState,
  WSLoader,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  Currency,
  RecurringDocumentStatus
} from "@wingspanhq/wingspan-api-sdk";
import { queriesRecurringInvoices } from "@query";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { INVOICING_PATH } from "..";
import { useBrowserPageTitle } from "../../../shared/hooks/useBrowserPageTitle";
import { useUrlQueryFilters } from "../../../utils/router";
import { TableInvoiceTemplates } from "../components/TableInvoiceTemplates";
import { Filters, Toolbar } from "../components/Toolbar";
import { selectorIsTopLevelRecurringInvoice } from "../selectors/selectorIsTopLevelRecurringInvoice";

const VISIBLE_STATUSES: Array<RecurringDocumentStatus | undefined> = [
  RecurringDocumentStatus.ACTIVE,
  RecurringDocumentStatus.DRAFT
];

export const RouteScheduled: React.FC = () => {
  useBrowserPageTitle("Scheduled invoices");
  const history = useHistory();
  const { defaultFilters, filters, setFilters } = useUrlQueryFilters<Filters>({
    memberClientId: "all"
  });

  const queryRecurringInvoices =
    queriesRecurringInvoices.listRecurringInvoices.useQueryAll();

  const recurringInvoices = useMemo(() => {
    const rows = (queryRecurringInvoices.flatData ?? []).filter(
      recurringInvoice =>
        selectorIsTopLevelRecurringInvoice(recurringInvoice) &&
        VISIBLE_STATUSES.includes(recurringInvoice.status) &&
        (filters.memberClientId === "all" ||
          recurringInvoice.payerId === filters.memberClientId)
    );

    return rows.sort(
      (a, b) =>
        (b.events?.createdAt?.valueOf() ?? 0) -
        (a.events?.createdAt?.valueOf() ?? 0)
    );
  }, [queryRecurringInvoices.flatData, filters.memberClientId]);

  return (
    <WSPage>
      <WSPageToolbar
        breadcrumbs={[
          {
            label: "Invoicing",
            onClick: () => {
              history.push(INVOICING_PATH);
            }
          },
          {
            label: "Scheduled"
          }
        ]}
        title="Scheduled invoices"
        actions={[
          {
            label: "Search",
            icon: "search",
            buttonKind: "Tertiary",
            onClick: () => history.push(`${INVOICING_PATH}/search`),
            hideButtonOn: false
          },
          {
            label: "Create invoice",
            icon: "plus",
            buttonKind: "Primary",
            onClick: () => history.push(INVOICING_PATH + "/create")
          }
        ]}
      />

      <Toolbar
        defaultFilters={defaultFilters}
        filters={filters}
        setFilters={setFilters}
        count={recurringInvoices.length}
        currency={recurringInvoices[0]?.currency as Currency | undefined}
        mb="L"
      />

      {queryRecurringInvoices.isLoading && <WSLoader.Spinner size="S" />}

      {queryRecurringInvoices.isError && (
        <WSEmptyState
          type="error"
          title="Couldn't load scheduled invoices"
          description="Something went wrong loading scheduled invoices."
          actions={{
            buttons: [
              {
                label: "Try again",
                onClick: () => queryRecurringInvoices.refetch()
              }
            ]
          }}
        />
      )}

      {!queryRecurringInvoices.isLoading &&
        !queryRecurringInvoices.isError &&
        recurringInvoices.length === 0 && (
          <WSText.ParagraphSm color="gray500">
            No scheduled invoices found
          </WSText.ParagraphSm>
        )}

      {!queryRecurringInvoices.isError &&
        (queryRecurringInvoices.isLoading || recurringInvoices.length > 0) && (
          <TableInvoiceTemplates
            recurringInvoices={recurringInvoices}
            loading={queryRecurringInvoices.isLoading}
          />
        )}
    </WSPage>
  );
};
